import React, { useMemo, useState } from "react";
import {
  Calendar,
  FileText,
  Star,
  Search as SearchIcon,
  X,
} from "lucide-react";

import AppointmentChat from "../telehealth/AppointmentChat";
import { auth } from "../../firebase";

/* ========== helpers ========== */
const formatAppointmentDate = (dateString, timeString) => {
  if (!dateString) return "Date TBD";
  try {
    const date = new Date(dateString);
    const dateStr = date.toLocaleDateString("sq-AL", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
    if (timeString) {
      const time = new Date(`2000-01-01 ${timeString}`).toLocaleTimeString(
        "sq-AL",
        {
          hour: "2-digit",
          minute: "2-digit",
        }
      );
      return `${dateStr} • ${time}`;
    }
    return dateStr;
  } catch {
    return "Invalid Date";
  }
};

const getStatusChip = (status) => {
  const s = (status || "").toLowerCase();
  if (s === "confirmed")
    return {
      bg: "#dcfce7",
      fg: "#166534",
      border: "#bbf7d0",
      label: "Konfirmuar",
    };
  if (s === "pending")
    return {
      bg: "#fef3c7",
      fg: "#92400e",
      border: "#fde68a",
      label: "Në pritje",
    };
  if (s === "completed")
    return {
      bg: "#dbeafe",
      fg: "#1e40af",
      border: "#bfdbfe",
      label: "Përfunduar",
    };
  if (s === "cancelled")
    return {
      bg: "#fee2e2",
      fg: "#991b1b",
      border: "#fecaca",
      label: "Anulluar",
    };
  return {
    bg: "#f3f4f6",
    fg: "#374151",
    border: "#e5e7eb",
    label: status || "Status",
  };
};

const typeLabel = (type) =>
  type === "video-call"
    ? "Video konsultë"
    : type === "phone-call"
    ? "Telefonatë"
    : "Vizitë në klinikë";

const typeIcon = (type) =>
  type === "video-call" ? "💻" : type === "phone-call" ? "📞" : "🏥";

const toJsDate = (v) => {
  if (!v) return null;
  if (v instanceof Date) return v;
  if (typeof v?.toDate === "function") return v.toDate();
  const d = new Date(v);
  return isNaN(d) ? null : d;
};

const buildDateTime = (scheduledDate, scheduledTime) => {
  const d = toJsDate(scheduledDate);
  if (!d) return null;
  if (!scheduledTime) return d;
  const [h, m] = (scheduledTime || "00:00")
    .split(":")
    .map((n) => parseInt(n || "0", 10));
  const out = new Date(d);
  out.setHours(h || 0, m || 0, 0, 0);
  return out;
};

/* ========== small atoms ========== */

const DoctorAvatar = ({ appointment }) => {
  const info = appointment.doctorInfo || {};
  const name = info.name || "Mjek";
  const src =
    info.photoURL ||
    info.photoUrl ||
    info.avatarUrl ||
    info.avatar ||
    info.imageUrl ||
    info.profileImage ||
    appointment.doctorPhotoURL ||
    appointment.doctorAvatar ||
    null;

  const initials = name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((x) => x[0]?.toUpperCase() || "")
    .join("");

  if (src) {
    return (
      <img
        src={src}
        alt={`Dr. ${name}`}
        onError={(e) => {
          e.currentTarget.style.display = "none";
          const sibling = e.currentTarget.nextSibling;
          if (sibling) sibling.style.display = "flex";
        }}
        style={{
          width: 44,
          height: 44,
          borderRadius: "50%",
          objectFit: "cover",
          border: "2px solid white",
        }}
      />
    );
  }

  return (
    <div
      style={{
        width: 44,
        height: 44,
        borderRadius: "50%",
        background: "#4F46E5",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#EEF2FF",
        fontWeight: 700,
        fontSize: 14,
      }}
    >
      {initials || "DR"}
    </div>
  );
};

const TabButton = ({ id, label, count, active, onClick }) => (
  <button
    type="button"
    onClick={() => onClick(id)}
    style={{
      padding: "6px 12px",
      borderRadius: 999,
      border: active ? "1px solid #2563EB" : "1px solid #E5E7EB",
      fontSize: 12,
      fontWeight: 600,
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      background: active ? "#EEF2FF" : "#FFFFFF",
      color: active ? "#1D4ED8" : "#4B5563",
    }}
  >
    <span>{label}</span>
    <span
      style={{
        fontSize: 11,
        padding: "2px 6px",
        borderRadius: 999,
        background: active ? "#2563EB" : "#F3F4F6",
        color: active ? "#FFFFFF" : "#4B5563",
      }}
    >
      {count}
    </span>
  </button>
);

/* =======================================================
 *  APPOINTMENTS MINI-APP
 * ======================================================= */
const AppointmentsSection = ({
  appointments = [],
  medicalRecords = [],
  onReviewSelect = () => {},
  onRecordSelect = () => {},
}) => {
  const [search, setSearch] = useState("");
  const [statusTab, setStatusTab] = useState("upcoming");
  const [chatAppointment, setChatAppointment] = useState(null);

  // records by appointment id
  const recordByApptId = useMemo(() => {
    const m = new Map();
    medicalRecords.forEach(
      (r) => r?.appointmentId && m.set(r.appointmentId, r)
    );
    return m;
  }, [medicalRecords]);

  const searchLower = search.trim().toLowerCase();
const filtered = useMemo(() => {
  const matchesStatusTab = (a) => {
    const s = (a?.status || "").toLowerCase();
    if (statusTab === "all") return true;
    if (statusTab === "completed") return s === "completed";
    if (statusTab === "cancelled") return s === "cancelled";
    if (statusTab === "upcoming") return s === "pending" || s === "confirmed";
    return true;
  };

  const matchesSearch = (a) => {
    if (!searchLower) return true;
    const hay = [
      a?.serviceName,
      a?.reason,
      a?.notes,
      a?.status,
      a?.appointmentType,
      a?.doctorInfo?.name,
      a?.doctorInfo?.specialty,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return hay.includes(searchLower);
  };

  // 🔁 NEW: latest → oldest
  const byDateDesc = (a, b) => {
    const ad =
      buildDateTime(a?.scheduledDate, a?.scheduledTime)?.getTime() ||
      toJsDate(a?.createdAt)?.getTime() ||
      0;
    const bd =
      buildDateTime(b?.scheduledDate, b?.scheduledTime)?.getTime() ||
      toJsDate(b?.createdAt)?.getTime() ||
      0;
    return bd - ad; // newest first
  };

  return appointments
    .filter(matchesStatusTab)
    .filter(matchesSearch)
    .sort(byDateDesc);
}, [appointments, searchLower, statusTab]);


  const counts = useMemo(() => {
    let upcoming = 0;
    let completed = 0;
    let cancelled = 0;
    appointments.forEach((a) => {
      const s = (a?.status || "").toLowerCase();
      if (s === "completed") completed++;
      else if (s === "cancelled") cancelled++;
      else if (s === "pending" || s === "confirmed") upcoming++;
    });
    return {
      all: appointments.length,
      upcoming,
      completed,
      cancelled,
    };
  }, [appointments]);

  /* ---------- render ---------- */

  return (
    <>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        {/* Header row */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div>
              <div
                style={{
                  fontSize: 12,
                  color: "#6B7280",
                }}
              >
                {counts.upcoming > 0
                  ? `Ke ${counts.upcoming} takim(e) në pritje.`
                  : "Nuk ke takime në pritje."}
              </div>
            </div>
          </div>
        </div>

        {/* Search + tabs */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 10,
            borderTop: "1px solid #E5E7EB",
            paddingTop: 10,
          }}
        >
          {/* search bar */}
          <div style={{ position: "relative" }}>
            <SearchIcon
              style={{
                position: "absolute",
                left: 10,
                top: "50%",
                transform: "translateY(-50%)",
                width: 16,
                height: 16,
                color: "#9CA3AF",
              }}
            />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Kërko shërbim, mjek ose arsye vizite…"
              aria-label="Kërko takimet"
              style={{
                width: "100%",
                padding: "8px 36px 8px 32px",
                borderRadius: 999,
                border: "1px solid #E5E7EB",
                fontSize: 13,
                outline: "none",
                boxSizing: "border-box",
              }}
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                aria-label="Pastro kërkimin"
                style={{
                  position: "absolute",
                  right: 8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  padding: 4,
                  borderRadius: 999,
                }}
              >
                <X style={{ width: 14, height: 14, color: "#9CA3AF" }} />
              </button>
            )}
          </div>

          {/* tabs */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 6,
            }}
          >
            <TabButton
              id="upcoming"
              label="Në pritje"
              count={counts.upcoming}
              active={statusTab === "upcoming"}
              onClick={setStatusTab}
            />
            <TabButton
              id="all"
              label="Të gjitha"
              count={counts.all}
              active={statusTab === "all"}
              onClick={setStatusTab}
            />
            <TabButton
              id="completed"
              label="Përfunduara"
              count={counts.completed}
              active={statusTab === "completed"}
              onClick={setStatusTab}
            />
            <TabButton
              id="cancelled"
              label="Anulluara"
              count={counts.cancelled}
              active={statusTab === "cancelled"}
              onClick={setStatusTab}
            />
          </div>
        </div>

        {/* List / empty states */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 10,
            marginTop: 6,
          }}
        >
          {appointments.length === 0 ? (
            <div style={{ textAlign: "center", padding: "24px 8px" }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 999,
                  background: "#EFF6FF",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#2563EB",
                  marginBottom: 8,
                }}
              >
                <Calendar style={{ width: 20, height: 20 }} />
              </div>
              <h3
                style={{
                  fontSize: 15,
                  fontWeight: 600,
                  color: "#111827",
                  marginBottom: 4,
                }}
              >
                Nuk ke ende asnjë takim
              </h3>
              <p
                style={{
                  color: "#6B7280",
                  margin: 0,
                  fontSize: 13,
                }}
              >
                Rezervo takimin e parë për të nisur historikun tënd.
              </p>
            </div>
          ) : filtered.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "20px 8px",
                borderRadius: 12,
                border: "1px dashed #E5E7EB",
                background: "#F9FAFB",
              }}
            >
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 40,
                  height: 40,
                  borderRadius: 999,
                  background: "#EEF2FF",
                  color: "#4F46E5",
                  marginBottom: 6,
                }}
              >
                <SearchIcon style={{ width: 18, height: 18 }} />
              </div>
              <p
                style={{
                  color: "#111827",
                  margin: "0 0 2px 0",
                  fontWeight: 600,
                  fontSize: 14,
                }}
              >
                Asnjë takim nuk përputhet me filtrat
              </p>
              <p
                style={{
                  color: "#6B7280",
                  margin: 0,
                  fontSize: 13,
                }}
              >
                Provo fjalë të tjera ose ndrysho statusin.
              </p>
            </div>
          ) : (
            filtered.map((appointment) => {
              const chip = getStatusChip(appointment.status);
              const record = recordByApptId.get(appointment.id);

              const statusLower = (appointment.status || "").toLowerCase();
              const isCompleted = statusLower === "completed";
              const isCancelled = statusLower === "cancelled";

              // 👇 this reads your reason from Firestore
              const cancelReason =
                appointment.cancelReason ||
                "";

              const created = toJsDate(appointment?.createdAt) || new Date();

              const handleCardClick = () => {
                if (isCompleted && record) onRecordSelect(record);
              };

              return (
                <div
                  key={appointment.id}
                  onClick={handleCardClick}
                  role={isCompleted && record ? "button" : undefined}
                  tabIndex={isCompleted && record ? 0 : -1}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && isCompleted && record)
                      handleCardClick();
                  }}
                  style={{
                    background: "#FFFFFF",
                    borderRadius: 12,
                    padding: 12,
                    border: "1px solid #E5E7EB",
                    cursor: isCompleted && record ? "pointer" : "default",
                  }}
                >
                  {/* top row: doctor + status */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 10,
                      marginBottom: 8,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                      }}
                    >
                      <div style={{ position: "relative" }}>
                        <DoctorAvatar appointment={appointment} />
                        <div
                          style={{
                            display: "none",
                            position: "absolute",
                            inset: 0,
                          }}
                        >
                          <DoctorAvatar appointment={appointment} />
                        </div>
                      </div>
                      <div>
                        <div
                          style={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: "#0F172A",
                          }}
                        >
                          Dr.{" "}
                          {appointment.doctorInfo?.name || "Mjek pa emër"}
                        </div>
                        <div
                          style={{
                            fontSize: 12,
                            color: "#6B7280",
                            marginTop: 1,
                          }}
                        >
                          {appointment.doctorInfo?.specialty ||
                            "Mjekësi e përgjithshme"}
                        </div>
                      </div>
                    </div>
                    <span
                      style={{
                        backgroundColor: chip.bg,
                        color: chip.fg,
                        padding: "3px 8px",
                        borderRadius: 999,
                        fontSize: 11,
                        fontWeight: 600,
                        border: `1px solid ${chip.border}`,
                      }}
                    >
                      {chip.label}
                    </span>
                  </div>

                  {/* middle: main info */}
                  <div
                    style={{
                      borderRadius: 10,
                      padding: 10,
                      border: "1px solid #E5E7EB",
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(160px, 1fr))",
                      gap: 8,
                      marginBottom: 6,
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: 11,
                          textTransform: "uppercase",
                          letterSpacing: 0.4,
                          color: "#6B7280",
                        }}
                      >
                        Shërbimi
                      </div>
                      <div
                        style={{
                          fontSize: 14,
                          fontWeight: 600,
                          color: "#111827",
                          marginTop: 2,
                        }}
                      >
                        {appointment.serviceName ||
                          "Konsultë e përgjithshme"}
                      </div>
                      {appointment.servicePrice && (
                        <div
                          style={{
                            marginTop: 3,
                            fontSize: 11,
                            display: "inline-flex",
                            padding: "2px 6px",
                            borderRadius: 6,
                            background: "#DCFCE7",
                            color: "#065F46",
                            fontWeight: 600,
                          }}
                        >
                          {appointment.servicePrice}
                        </div>
                      )}
                    </div>

                    <div>
                      <div
                        style={{
                          fontSize: 11,
                          textTransform: "uppercase",
                          letterSpacing: 0.4,
                          color: "#6B7280",
                        }}
                      >
                        Data & ora
                      </div>
                      <div
                        style={{
                          fontSize: 14,
                          fontWeight: 600,
                          color: "#111827",
                          marginTop: 2,
                        }}
                      >
                        {formatAppointmentDate(
                          appointment.scheduledDate,
                          appointment.scheduledTime
                        )}
                      </div>
                    </div>

                    <div>
                      <div
                        style={{
                          fontSize: 11,
                          textTransform: "uppercase",
                          letterSpacing: 0.4,
                          color: "#6B7280",
                        }}
                      >
                        Lloji i takimit
                      </div>
                      <div
                        style={{
                          fontSize: 14,
                          fontWeight: 600,
                          color: "#111827",
                          marginTop: 2,
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        <span>{typeIcon(appointment.appointmentType)}</span>
                        {typeLabel(appointment.appointmentType)}
                      </div>
                    </div>
                  </div>

                  {/* optional visit reason */}
                  {appointment.reason && (
                    <div
                      style={{
                        marginBottom: 6,
                        fontSize: 13,
                        color: "#374151",
                      }}
                    >
                      <span
                        style={{
                          fontSize: 11,
                          textTransform: "uppercase",
                          letterSpacing: 0.4,
                          color: "#6B7280",
                        }}
                      >
                        Arsyeja e vizitës
                      </span>
                      <div style={{ marginTop: 2 }}>{appointment.reason}</div>
                    </div>
                  )}

                  {/* 🔴 cancellation reason — visible only if cancelled & reason exists */}
                  {isCancelled && cancelReason && (
                    <div
                      style={{
                        marginBottom: 6,
                        fontSize: 13,
                        color: "#991B1B",
                        backgroundColor: "#FEF2F2",
                        borderRadius: 8,
                        border: "1px solid #FECACA",
                        padding: 8,
                      }}
                    >
                      <span
                        style={{
                          fontSize: 11,
                          textTransform: "uppercase",
                          letterSpacing: 0.4,
                          color: "#B91C1C",
                        }}
                      >
                        Arsyeja e anulimit
                      </span>
                      <div style={{ marginTop: 2 }}>{cancelReason}</div>
                    </div>
                  )}

                  {/* footer: requested + actions */}
                  <div
                    style={{
                      paddingTop: 6,
                      borderTop: "1px solid #E5E7EB",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 11,
                        color: "#9CA3AF",
                      }}
                    >
                      Kërkuar më{" "}
                      {created.toLocaleDateString("sq-AL", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                      })}
                    </span>

                    <div
                      style={{
                        display: "flex",
                        gap: 6,
                        flexWrap: "wrap",
                        justifyContent: "flex-end",
                      }}
                    >
                      {/* chat */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setChatAppointment(appointment);
                        }}
                        style={{
                          background: "#2563EB",
                          color: "white",
                          border: "none",
                          padding: "6px 10px",
                          borderRadius: 999,
                          fontSize: 11,
                          fontWeight: 600,
                          cursor: "pointer",
                        }}
                      >
                        💬 Bisedo me mjekun
                      </button>

                      {isCompleted && (
                        <>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              record
                                ? onRecordSelect(record)
                                : alert(
                                    "Nuk ka ende një raport mjekësor të lidhur me këtë takim."
                                  );
                            }}
                            style={{
                              background: "#7C3AED",
                              color: "white",
                              border: "none",
                              padding: "6px 10px",
                              borderRadius: 999,
                              fontSize: 11,
                              fontWeight: 600,
                              cursor: "pointer",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                            }}
                          >
                            <FileText style={{ width: 12, height: 12 }} />
                            {record ? "Shiko raportin" : "Pa raport"}
                          </button>

                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onReviewSelect(appointment);
                            }}
                            style={{
                              background: "#F59E0B",
                              color: "white",
                              border: "none",
                              padding: "6px 10px",
                              borderRadius: 999,
                              fontSize: 11,
                              fontWeight: 600,
                              cursor: "pointer",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                            }}
                          >
                            <Star style={{ width: 12, height: 12 }} />
                            Shkruaj vlerësim
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Floating telehealth chat window */}
      {chatAppointment && auth.currentUser && (
        <div
          style={{
            position: "fixed",
            right: 16,
            bottom: 16,
            zIndex: 9999,
          }}
        >
          <AppointmentChat
            appointmentId={chatAppointment.id}
            doctorId={
              chatAppointment.doctorInfo?.uid ||
              chatAppointment.doctorInfo?.id ||
              chatAppointment.doctorId
            }
            patientId={auth.currentUser.uid}
            appointmentStatus={chatAppointment.status}
            doctorName={chatAppointment.doctorInfo?.name}
            patientName={chatAppointment.patientName}
            serviceName={chatAppointment.serviceName}
            onClose={() => setChatAppointment(null)}
          />
        </div>
      )}
    </>
  );
};

export default AppointmentsSection;
